import React, { useCallback, useMemo, useState, createElement, Component } from 'react';
import { Maximize2Icon, XIcon, ChevronDownIcon, ChevronRightIcon, CopyIcon, CheckIcon } from 'lucide-react';
interface DiffRow {
  type: 'context' | 'add' | 'remove' | 'modify' | 'hunk';
  leftNum: number | null;
  leftContent: string;
  rightNum: number | null;
  rightContent: string;
  hunkShort?: string;
  hunkIndex?: number;
}
function parsePatchToSideBySide(patch: string): DiffRow[] {
  const lines = patch.split('\n');
  const rows: DiffRow[] = [];
  let oldLine = 0;
  let newLine = 0;
  let removeBuffer: string[] = [];
  let addBuffer: string[] = [];
  let removeStartLine = 0;
  let addStartLine = 0;
  let currentHunk = -1;
  const flushBuffers = () => {
    const maxLen = Math.max(removeBuffer.length, addBuffer.length);
    if (maxLen === 0) return;
    for (let i = 0; i < maxLen; i++) {
      const hasRemove = i < removeBuffer.length;
      const hasAdd = i < addBuffer.length;
      let type: DiffRow['type'] = 'context';
      if (hasRemove && hasAdd) type = 'modify';else if (hasRemove) type = 'remove';else if (hasAdd) type = 'add';
      rows.push({
        type,
        leftNum: hasRemove ? removeStartLine + i : null,
        leftContent: hasRemove ? removeBuffer[i] : '',
        rightNum: hasAdd ? addStartLine + i : null,
        rightContent: hasAdd ? addBuffer[i] : '',
        hunkIndex: currentHunk
      });
    }
    removeBuffer = [];
    addBuffer = [];
  };
  for (const line of lines) {
    if (line.startsWith('@@')) {
      flushBuffers();
      const match = line.match(/@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@(.*)/);
      if (match) {
        oldLine = parseInt(match[1], 10);
        newLine = parseInt(match[2], 10);
        currentHunk++;
        const shortHunk = `@@ -${match[1]}${line.match(/@@ -\d+(,\d+)?/)?.[1] || ''} +${match[2]}${line.match(/\+\d+(,\d+)?/)?.[1] || ''} @@`;
        rows.push({
          type: 'hunk',
          leftNum: null,
          leftContent: line,
          rightNum: null,
          rightContent: line,
          hunkShort: shortHunk,
          hunkIndex: currentHunk
        });
      }
      continue;
    }
    if (line.startsWith('-')) {
      if (removeBuffer.length === 0) removeStartLine = oldLine;
      removeBuffer.push(line.slice(1));
      oldLine++;
    } else if (line.startsWith('+')) {
      if (addBuffer.length === 0) addStartLine = newLine;
      addBuffer.push(line.slice(1));
      newLine++;
    } else {
      flushBuffers();
      const content = line.startsWith(' ') ? line.slice(1) : line;
      rows.push({
        type: 'context',
        leftNum: oldLine,
        leftContent: content,
        rightNum: newLine,
        rightContent: content,
        hunkIndex: currentHunk
      });
      oldLine++;
      newLine++;
    }
  }
  flushBuffers();
  return rows;
}
/* ── Shared button style ────────────────────────────────────────────── */
const iconBtnStyle: React.CSSProperties = {
  padding: 4,
  background: 'transparent',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'rgba(96, 165, 250, 0.7)',
  flexShrink: 0
};
/* ── Renderer ───────────────────────────────────────────────────────── */
function DiffRenderer({
  rows,
  maxHeight,
  fullscreen,
  collapsedHunks,
  onToggleHunk
}: {
  rows: DiffRow[];
  maxHeight?: number;
  fullscreen?: boolean;
  collapsedHunks: Set<number>;
  onToggleHunk: (idx: number) => void;
}) {
  return <div style={{
    maxHeight: maxHeight ?? undefined,
    overflowY: 'auto',
    overflowX: 'hidden',
    width: '100%',
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
    fontSize: 12
  }} data-id="element-2639">
      {rows.map((row, i) => {
      if (row.type === 'hunk') {
        const hIdx = row.hunkIndex ?? 0;
        const isCollapsed = collapsedHunks.has(hIdx);
        return <div key={i} style={{
          background: 'rgba(59, 130, 246, 0.06)',
          borderTop: '1px solid rgba(59, 130, 246, 0.15)',
          borderBottom: '1px solid rgba(59, 130, 246, 0.15)',
          padding: '1px 8px',
          color: 'rgb(96, 165, 250)',
          fontSize: 11,
          fontWeight: 500,
          lineHeight: '22px',
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-2640">
              <button onClick={() => onToggleHunk(hIdx)} style={iconBtnStyle} title={isCollapsed ? 'Expandir hunk' : 'Colapsar hunk'} data-id="element-2641">
                {isCollapsed ? <ChevronRightIcon size={14} data-id="element-2642" /> : <ChevronDownIcon size={14} data-id="element-2643" />}
              </button>
              <span data-id="element-2644">{row.hunkShort || row.leftContent}</span>
              {isCollapsed && <span style={{
            color: 'rgba(96, 165, 250, 0.4)',
            fontSize: 10,
            marginLeft: 8
          }} data-id="element-2645">
                  (colapsado)
                </span>}
            </div>;
      }
      // Skip rows if their hunk is collapsed
      if (row.hunkIndex !== undefined && collapsedHunks.has(row.hunkIndex)) {
        return null;
      }
      const isRem = row.type === 'remove' || row.type === 'modify';
      const isAdd = row.type === 'add' || row.type === 'modify';
      return <div key={i} style={{
        display: 'flex',
        width: '100%',
        minHeight: 20
      }} data-id="element-2646">
            {/* ── LEFT HALF ── */}
            <div style={{
          width: '50%',
          display: 'flex',
          borderRight: '1px solid rgba(100, 116, 139, 0.25)',
          background: isRem ? 'rgba(239, 68, 68, 0.08)' : undefined
        }} data-id="element-2647">
              <div style={{
            width: 40,
            minWidth: 40,
            textAlign: 'right',
            paddingRight: 8,
            lineHeight: '20px',
            userSelect: 'none',
            color: isRem ? 'rgba(239, 68, 68, 0.4)' : 'rgba(100, 116, 139, 0.5)'
          }} data-id="element-2648">
                {row.leftNum ?? ''}
              </div>
              <div style={{
            flex: 1,
            overflow: 'hidden',
            position: 'relative'
          }} data-id="element-2649">
                <div style={{
              overflowX: fullscreen ? 'auto' : 'hidden',
              whiteSpace: 'pre',
              lineHeight: '20px',
              paddingLeft: 4,
              paddingRight: 8,
              color: isRem ? 'rgb(252, 165, 165)' : row.type === 'add' ? 'transparent' : 'rgb(148, 163, 184)'
            }} data-id="element-2650">
                  {isRem ? `- ${row.leftContent}` : row.type === 'add' ? ' ' : `  ${row.leftContent}`}
                </div>
              </div>
            </div>

            {/* ── RIGHT HALF ── */}
            <div style={{
          width: '50%',
          display: 'flex',
          background: isAdd ? 'rgba(16, 185, 129, 0.08)' : undefined
        }} data-id="element-2651">
              <div style={{
            width: 40,
            minWidth: 40,
            textAlign: 'right',
            paddingRight: 8,
            lineHeight: '20px',
            userSelect: 'none',
            color: isAdd ? 'rgba(16, 185, 129, 0.4)' : 'rgba(100, 116, 139, 0.5)'
          }} data-id="element-2652">
                {row.rightNum ?? ''}
              </div>
              <div style={{
            flex: 1,
            overflow: 'hidden',
            position: 'relative'
          }} data-id="element-2653">
                <div style={{
              overflowX: fullscreen ? 'auto' : 'hidden',
              whiteSpace: 'pre',
              lineHeight: '20px',
              paddingLeft: 4,
              paddingRight: 8,
              color: isAdd ? 'rgb(110, 231, 183)' : row.type === 'remove' ? 'transparent' : 'rgb(148, 163, 184)'
            }} data-id="element-2654">
                  {isAdd ? `+ ${row.rightContent}` : row.type === 'remove' ? ' ' : `  ${row.rightContent}`}
                </div>
              </div>
            </div>
          </div>;
    })}
    </div>;
}
/* ── Copy Button ────────────────────────────────────────────────────── */
function CopyButton({
  text
}: {
  text: string;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [text]);
  return <button onClick={handleCopy} style={{
    padding: '4px 8px',
    background: copied ? 'rgba(16, 185, 129, 0.15)' : 'rgba(30, 41, 59, 0.9)',
    border: `1px solid ${copied ? 'rgba(16, 185, 129, 0.3)' : 'rgba(51, 65, 85, 0.5)'}`,
    borderRadius: 6,
    color: copied ? 'rgb(110, 231, 183)' : 'rgb(148, 163, 184)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    fontFamily: 'inherit',
    transition: 'all 0.15s ease'
  }} title="Copiar diff" data-id="element-2655">
      {copied ? <CheckIcon size={12} data-id="element-2656" /> : <CopyIcon size={12} data-id="element-2657" />}
      {copied ? 'Copiado!' : 'Copiar'}
    </button>;
}
/* ── Main Component ─────────────────────────────────────────────────── */
interface SideBySideDiffProps {
  patch: string;
  maxHeight?: number;
  filename?: string;
  fullscreen?: boolean;
  onFullscreenChange?: (fs: boolean) => void;
}
export function SideBySideDiff({
  patch,
  maxHeight = 400,
  filename,
  fullscreen: controlledFullscreen,
  onFullscreenChange
}: SideBySideDiffProps) {
  const rows = useMemo(() => parsePatchToSideBySide(patch), [patch]);
  const [internalFullscreen, setInternalFullscreen] = useState(false);
  const [collapsedHunks, setCollapsedHunks] = useState<Set<number>>(new Set());
  // Support both controlled and uncontrolled fullscreen
  const isControlled = controlledFullscreen !== undefined;
  const fullscreen = isControlled ? controlledFullscreen : internalFullscreen;
  const setFullscreen = (val: boolean) => {
    if (onFullscreenChange) onFullscreenChange(val);
    if (!isControlled) setInternalFullscreen(val);
  };
  const toggleHunk = useCallback((idx: number) => {
    setCollapsedHunks(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);else next.add(idx);
      return next;
    });
  }, []);
  const toolbarStyle: React.CSSProperties = {
    position: 'absolute',
    top: 6,
    right: 6,
    zIndex: 10,
    display: 'flex',
    gap: 4,
    alignItems: 'center'
  };
  const expandBtnStyle: React.CSSProperties = {
    padding: 6,
    background: 'rgba(30, 41, 59, 0.9)',
    border: '1px solid rgba(51, 65, 85, 0.5)',
    borderRadius: 6,
    color: 'rgb(148, 163, 184)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };
  return <>
      <div style={{
      position: 'relative',
      background: '#0d1117',
      width: '100%',
      overflow: 'hidden'
    }} data-id="element-2658">
        {/* Toolbar: Fullscreen only — hide if controlled externally */}
        {!isControlled && <div style={toolbarStyle} data-id="element-2659">
            <button onClick={() => setFullscreen(true)} style={expandBtnStyle} title="Ver en pantalla completa" data-id="element-2660">
              <Maximize2Icon size={14} data-id="element-2661" />
            </button>
          </div>}
        <DiffRenderer rows={rows} maxHeight={maxHeight} collapsedHunks={collapsedHunks} onToggleHunk={toggleHunk} data-id="element-2662" />
      </div>

      {fullscreen && <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 100,
      background: '#0d1117',
      display: 'flex',
      flexDirection: 'column'
    }} data-id="element-2663">
          <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 16px',
        borderBottom: '1px solid rgb(30, 41, 59)',
        background: 'rgba(15, 23, 42, 0.8)'
      }} data-id="element-2664">
            <span style={{
          fontSize: 14,
          fontWeight: 500,
          color: 'rgb(203, 213, 225)',
          fontFamily: 'ui-monospace, SFMono-Regular, monospace'
        }} data-id="element-2665">
              {filename || 'Diff viewer'}
            </span>
            <div style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center'
        }} data-id="element-2666">
              <CopyButton text={patch} data-id="element-2667" />
              <button onClick={() => setFullscreen(false)} style={{
            padding: 8,
            color: 'rgb(148, 163, 184)',
            background: 'transparent',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            display: 'flex'
          }} data-id="element-2668">
                <XIcon size={18} data-id="element-2669" />
              </button>
            </div>
          </div>
          <div style={{
        flex: 1,
        overflow: 'auto'
      }} data-id="element-2670">
            <DiffRenderer rows={rows} fullscreen collapsedHunks={collapsedHunks} onToggleHunk={toggleHunk} data-id="element-2671" />
          </div>
        </div>}
    </>;
}